import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { AppLauncher } from '@capacitor/app-launcher';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

declare var YT: any;

@Component({
  selector: 'app-video-popover',
  templateUrl: './video-popover.component.html',
  styleUrls: ['./video-popover.component.scss'],
})
export class VideoPopoverComponent implements OnInit, OnDestroy {
    @Input() videoUrl: any;
  @Input() youtube: boolean = false;
  isLoading: boolean = true;
  player: any;
  videoId: string;
  hasError: boolean = false;
  platform: string;

  constructor(
    private popoverController: PopoverController,
    private cdRef: ChangeDetectorRef
  ) {
    this.platform = Capacitor.getPlatform();
  }

  ngOnInit() {
    if (this.youtube && this.videoUrl) {
      // Extraer el ID del video de YouTube
      this.videoId = this.extractVideoId(this.videoUrl);

      if (!this.videoId) {
        console.error('No se pudo extraer el ID del video de YouTube');
        this.isLoading = false;
        this.hasError = true;
        this.cdRef.detectChanges();
        return;
      }

      // Cargar la API de YouTube si aún no está cargada
      if (!window['YT']) {
        this.loadYouTubeAPI();
      } else if (window['YT'].Player) {
        // Si ya está cargada, crear el reproductor directamente
        this.createPlayer();
      }
    }
  }

  ngOnDestroy() {
    // Destruir el reproductor al cerrar el componente
    if (this.player && this.player.destroy) {
      this.player.destroy();
    }
  }

  private loadYouTubeAPI() {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    // Definir la función callback global que YouTube API llamará
    (window as any).onYouTubeIframeAPIReady = () => {
      this.createPlayer();
    };
  }

  private createPlayer() {
    // Pequeño delay para asegurar que el DOM esté listo
    setTimeout(() => {
      this.player = new YT.Player('youtube-player', {
        height: '100%',
        width: '100%',
        videoId: this.videoId,
        playerVars: {
          autoplay: 1,
          playsinline: 1,
          modestbranding: 1,
          rel: 0
        },
        events: {
          onReady: this.onPlayerReady.bind(this),
          onStateChange: this.onPlayerStateChange.bind(this),
          onError: this.onPlayerError.bind(this)
        }
      });
    }, 100);
  }

  private onPlayerReady(event: any) {
    this.isLoading = false;
    this.cdRef.detectChanges();
    // El video se reproduce automáticamente gracias a autoplay: 1
  }

  private onPlayerStateChange(event: any) {
    // Cuando el video termina (state = 0), cerrar el popover
    if (event.data === YT.PlayerState.ENDED) {
      this.dismiss();
    }
  }

  private onPlayerError(event: any) {
    console.error('Error en el reproductor de YouTube:', event);
    this.isLoading = false;
    this.hasError = true;
    this.cdRef.detectChanges();
  }

  private extractVideoId(url: string): string {
    // Extraer ID de varias formas de URLs de YouTube
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([^&\n?#]+)$/ // Por si solo pasan el ID
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }

    return '';
  }

  async dismiss() {
    // Pausar el video antes de cerrar
    if (this.player && this.player.pauseVideo) {
      this.player.pauseVideo();
    }
    await this.popoverController.dismiss('close');
  }

  load() {
    this.isLoading = false;
  }

  async openInExternal() {
    // Para videos de YouTube
    if (this.youtube && this.videoId) {
      const appScheme = 'com.google.android.youtube';
      const webUrl = `https://www.youtube.com/watch?v=${this.videoId}`;

      // Si es web, abrir directamente en navegador
      if (this.platform === 'web') {
        window.open(webUrl, '_blank');
        return;
      }

      // Para dispositivos móviles, intentar abrir la app de YouTube
      try {
        const result = await AppLauncher.canOpenUrl({ url: appScheme });

        if (result.value) {
          // Pausar el video antes de abrir la app externa
          if (this.player && this.player.pauseVideo) {
            this.player.pauseVideo();
          }
          await AppLauncher.openUrl({ url: webUrl });
          // Cerrar el popover después de abrir la app
          await this.dismiss();
        } else {
          // Si no tiene la app, abrir en el navegador
          await Browser.open({ url: webUrl });
          await this.dismiss();
        }
      } catch (err) {
        // En caso de error, abrir en el navegador
        await Browser.open({ url: webUrl });
        await this.dismiss();
      }
    } else if (!this.youtube && this.videoUrl) {
      // Para videos que no son de YouTube, abrir directamente en el navegador
      if (this.platform === 'web') {
        window.open(this.videoUrl, '_blank');
      } else {
        await Browser.open({ url: this.videoUrl });
        await this.dismiss();
      }
    }
  }
}
